// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { workspaceAPI, adminAPI, groupAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, 
  Users, 
  UserPlus,
  UserMinus,
  Loader2,
  Search,
  Mail,
  UsersRound,
  GraduationCap
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, getInitials } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ParticipantsPage() {
  const { workspaceId } = useParams();
  const { canManage, isAdmin } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState("users");
  const [searchTerm, setSearchTerm] = useState("");
  const [adding, setAdding] = useState(null);
  const [addingGroup, setAddingGroup] = useState(null);
  const [removeId, setRemoveId] = useState(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  const loadData = async () => {
    try {
      const [participantsRes, usersRes, groupsRes] = await Promise.all([
        workspaceAPI.listParticipants(workspaceId),
        canManage ? adminAPI.listUsers() : Promise.resolve({ data: [] }),
        canManage ? groupAPI.list() : Promise.resolve({ data: [] }),
      ]);
      setParticipants(participantsRes.data);
      setAllUsers(usersRes.data);
      setGroups(groupsRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipant = async (userId) => {
    setAdding(userId);
    try {
      await workspaceAPI.addParticipant(workspaceId, userId);
      const addedUser = allUsers.find(u => u.id === userId);
      if (addedUser) {
        setParticipants([...participants, addedUser]);
      }
      toast.success("Participant ajouté");
    } catch (error) {
      console.error("Failed to add participant:", error);
      toast.error("Erreur lors de l'ajout");
    } finally {
      setAdding(null);
    }
  };

  const handleAddGroup = async (groupId) => {
    setAddingGroup(groupId);
    try {
      // Get group members
      const membersRes = await groupAPI.listMembers(groupId);
      const members = membersRes.data;
      let addedCount = 0;
      
      for (const member of members) {
        // Skip if already a participant
        if (!participants.find(p => p.id === member.id)) {
          try {
            await workspaceAPI.addParticipant(workspaceId, member.id);
            addedCount++;
          } catch (e) {
            // Skip duplicate errors
          }
        }
      }
      
      if (addedCount > 0) {
        toast.success(`${addedCount} membre(s) ajouté(s) depuis le groupe`);
        loadData();
      } else {
        toast.info("Tous les membres de ce groupe sont déjà participants");
      }
    } catch (error) {
      console.error("Failed to add group:", error);
      toast.error("Erreur lors de l'ajout du groupe");
    } finally {
      setAddingGroup(null);
    }
  };

  const handleRemoveParticipant = async () => {
    if (!removeId) return;
    setRemoving(true);
    try {
      await workspaceAPI.removeParticipant(workspaceId, removeId);
      setParticipants(participants.filter(p => p.id !== removeId));
      toast.success("Participant retiré");
    } catch (error) {
      console.error("Failed to remove participant:", error);
      toast.error("Erreur lors du retrait");
    } finally {
      setRemoving(false);
      setRemoveId(null);
    }
  };

  // Filter available users (candidates + trainers not already in workspace)
  const getFilteredUsers = (roleFilter = null) => {
    return allUsers.filter(u => {
      const notAlreadyParticipant = !participants.find(p => p.id === u.id);
      const matchesSearch = u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter ? u.role === roleFilter : (u.role === 'candidate' || u.role === 'trainer');
      return notAlreadyParticipant && matchesSearch && matchesRole;
    });
  };

  const filteredCandidates = getFilteredUsers('candidate');
  const filteredTrainers = getFilteredUsers('trainer');
  const filteredGroups = groups.filter(g =>
    g.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const variants = {
      admin: "default",
      trainer: "secondary",
      candidate: "outline",
    };
    const labels = {
      admin: "Admin",
      trainer: "Formateur",
      candidate: "Candidat",
    };
    return <Badge variant={variants[role]}>{labels[role]}</Badge>;
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 skeleton rounded-lg" />
            ))}
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Participants</h1>
            <p className="mt-1 text-muted-foreground">
              {participants.length} participant(s) dans cet espace
            </p>
          </div>
          {canManage && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-participant-btn">
                  <UserPlus className="h-4 w-4" />
                  Ajouter
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Ajouter des participants</DialogTitle>
                </DialogHeader>
                <Tabs value={dialogTab} onValueChange={(v) => { setDialogTab(v); setSearchTerm(""); }}>
                  <TabsList className="w-full">
                    <TabsTrigger value="users" className="flex-1 gap-1">
                      <Users className="h-3.5 w-3.5" />
                      Candidats
                    </TabsTrigger>
                    <TabsTrigger value="trainers" className="flex-1 gap-1">
                      <GraduationCap className="h-3.5 w-3.5" />
                      Formateurs
                    </TabsTrigger>
                    <TabsTrigger value="groups" className="flex-1 gap-1">
                      <UsersRound className="h-3.5 w-3.5" />
                      Groupes
                    </TabsTrigger>
                  </TabsList>

                  <div className="mt-3 space-y-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="search-user-input"
                      />
                    </div>

                    {/* Candidates Tab */}
                    <TabsContent value="users" className="mt-0">
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {filteredCandidates.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            Aucun candidat disponible
                          </p>
                        ) : (
                          filteredCandidates.map((user) => (
                            <div 
                              key={user.id}
                              className="flex items-center gap-3 p-3 rounded-lg border hover:border-primary/50 transition-colors"
                            >
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className="bg-primary/10 text-primary">
                                  {getInitials(user.first_name, user.last_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">
                                  {user.first_name} {user.last_name}
                                </p>
                                <p className="text-sm text-muted-foreground truncate">
                                  {user.email}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => handleAddParticipant(user.id)}
                                disabled={adding === user.id}
                              >
                                {adding === user.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Plus className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </TabsContent>

                    {/* Trainers Tab */}
                    <TabsContent value="trainers" className="mt-0">
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {filteredTrainers.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            Aucun formateur disponible
                          </p>
                        ) : (
                          filteredTrainers.map((user) => (
                            <div 
                              key={user.id}
                              className="flex items-center gap-3 p-3 rounded-lg border hover:border-primary/50 transition-colors"
                            >
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className="bg-secondary text-secondary-foreground">
                                  {getInitials(user.first_name, user.last_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">
                                  {user.first_name} {user.last_name}
                                </p>
                                <p className="text-sm text-muted-foreground truncate">
                                  {user.email}
                                </p>
                              </div>
                              <Badge variant="secondary" className="mr-2">Formateur</Badge>
                              <Button
                                size="sm"
                                onClick={() => handleAddParticipant(user.id)}
                                disabled={adding === user.id}
                              >
                                {adding === user.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Plus className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </TabsContent>

                    {/* Groups Tab */}
                    <TabsContent value="groups" className="mt-0">
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {filteredGroups.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            Aucun groupe disponible
                          </p>
                        ) : (
                          filteredGroups.map((group) => (
                            <div 
                              key={group.id}
                              className="flex items-center gap-3 p-3 rounded-lg border hover:border-primary/50 transition-colors"
                            >
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                                <UsersRound className="h-5 w-5 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">{group.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {group.members?.length || 0} membre(s)
                                </p>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => handleAddGroup(group.id)}
                                disabled={addingGroup === group.id}
                              >
                                {addingGroup === group.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Plus className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </TabsContent>
                  </div>
                </Tabs>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Participants List */}
        {participants.length === 0 ? (
          <Card className="mt-8 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Users className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 font-semibold">Aucun participant</h3>
              <p className="mt-2 text-sm text-muted-foreground text-center">
                Ajoutez des participants pour qu'ils puissent accéder à cet espace
              </p>
              {canManage && (
                <Button className="mt-4 gap-2" onClick={() => setDialogOpen(true)}>
                  <UserPlus className="h-4 w-4" />
                  Ajouter un participant
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="mt-8 grid gap-4">
            {participants.map((participant, index) => (
              <Card 
                key={participant.id}
                data-testid={`participant-card-${index}`}
              >
                <CardContent className="flex items-center gap-4 p-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(participant.first_name, participant.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">
                        {participant.first_name} {participant.last_name}
                      </p>
                      {getRoleBadge(participant.role)}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      <span>{participant.email}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Inscrit le {formatDate(participant.created_at)}
                    </p>
                  </div>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setRemoveId(participant.id)}
                      data-testid={`remove-participant-${index}`}
                    >
                      <UserMinus className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Remove Confirmation Dialog */}
        <AlertDialog open={!!removeId} onOpenChange={() => setRemoveId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Retirer ce participant ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette personne n'aura plus accès à cet espace de travail.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleRemoveParticipant} disabled={removing}>
                {removing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Retirer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </WorkspaceLayout>
  );
}
